/**
 * DetailHeader · 详情页 sticky 标题区
 *
 * 顶部布局（左到右）：
 *  · 左：返回按钮 + 标题区
 *      标题：文件夹名（首期不开启 inline 重命名 · 留 TODO 等下一版）
 *      meta：规则提示条（hasRule 时，纯展示不可点） + 张数 + 子文件夹数
 *  · 右：对齐列表页按钮组（Figma 14:6054）：
 *      添加照片 / 新建文件夹 / AI 智能分类 / ⋯ 更多（下载记录）
 *
 * 规则提示条用 ruleText.tsx 的 renderRuleText 拼接 + 蓝色高亮。
 */

import React from 'react'
import { Breadcrumb, Button, Dropdown, Menu, Tooltip } from 'antd'
import {
  CaretDownOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FolderAddOutlined,
  FolderOutlined,
  PlusOutlined
} from '@ant-design/icons'
import classNames from 'classnames'
import AITriggerButton from '../components/AISidebar/AITriggerButton'
import MoreActionsMenu from '../components/MoreActionsMenu'
import { renderRuleText, buildRuleText } from './utils/ruleText'
import type { ClassifyRule } from './utils/ruleText'
import styles from './Detail.module.scss'

interface DetailHeaderProps {
  folderName: string
  /**
   * 文件夹类型 · 后端枚举，0=普通文件夹，>0 为后端定义的其他类型。
   * 目前前端仅用它做「新建子文件夹」门控：父页面按 isAdmin && folderType===0
   * 算出 canCreateSubFolder 传入。
   * 注意：是否为 AI 智能分类文件夹由 aiGenerated（后端 isIntelligenceFolder）单独标记，
   * 与本字段无关，两者互不派生——别把 folderType>0 当成「AI 文件夹」来判断。
   */
  folderType: number
  /**
   * 父级链 · 由后端 getFolderInfo 返回，约定 root → 近祖先顺序
   * 不含当前文件夹（folderName 单独入参）；面包屑会在末尾追加当前节点
   */
  parentFolders: Array<{ folderID: number; folderName: string }>
  photoCount: number
  subFolderCount: number
  rule: ClassifyRule | null | undefined
  /** 是否允许新建子文件夹 · 由父页面综合 isAdmin + folderType 判断后传入 */
  canCreateSubFolder?: boolean
  /** 是否在照片多选态 · 进入后顶部按钮换成「取消多选」 */
  photoSelectMode?: boolean
  /** 是否显示「多选」按钮 · 通常 photoCount > 0 时才有意义 */
  canSelectPhotos?: boolean
  onBack: () => void
  onAddPhoto: () => void
  /** 「新建文件夹」点击（在当前文件夹下新建子文件夹）· canCreateSubFolder=true 时按钮可见 */
  onCreateSubFolder: () => void
  /** 「多选」按钮点击 · 切进多选模式（或退出） */
  onTogglePhotoSelect: () => void
  /**
   * 面包屑跳转 · "照片分类"根节点 → 调用 onBack（同 path）；中间任一祖先 →
   * 跳到对应详情页。当前节点不可点。父页面（Detail/index.tsx）已有 push 句柄，
   * 把跳转逻辑包成 navigateToFolder 传进来即可。
   */
  onNavigateToFolder: (folderID: number) => void
  /** AI 助手 sidebar 是否已打开 · 决定 AI 助手按钮是否显示（开了就藏起来） */
  aiSidebarOpen?: boolean
  /** AI 助手 toggle · sidebar 关闭态点头部按钮唤起；详情页有自己的 sidebar 实例 */
  onToggleAISidebar?: () => void
  /** 「下载记录」点击（⋯ 更多菜单内）· 唤起下载记录抽屉，dispatch 与埋点在父页面 */
  onOpenDownloadHistory?: () => void
  /**
   * 是否允许操作当前文件夹（重命名 / 删除 / 移动）· 通常 = isAdmin
   * 不传或 false 时面包屑当前节点的 caret-down 菜单仅保留"下载"
   */
  canManageFolder?: boolean
  /** 面包屑当前节点的下拉菜单回调 · 与列表页右键单条菜单语义一致 */
  onFolderDownload?: () => void
  onFolderRename?: () => void
  onFolderDelete?: () => void
  onFolderMove?: () => void
  /**
   * 照片多选批量操作的 portal 槽位 ref 回调 · ref 拿到 DOM 节点交给 Detail/index.tsx
   * 以便后者把 [全选 / 已选 N / 下载 / 导出 Excel / 编辑水印 / 去水印 / 移动]
   * 一组按钮 createPortal 进去
   */
  onPhotoActionsSlotRef?: (el: HTMLSpanElement | null) => void
}

const DetailHeader: React.FC<DetailHeaderProps> = ({
  folderName,
  folderType,
  parentFolders,
  photoCount,
  subFolderCount,
  rule,
  canCreateSubFolder = false,
  photoSelectMode = false,
  canSelectPhotos = false,
  onBack,
  onAddPhoto,
  onCreateSubFolder,
  onTogglePhotoSelect,
  onNavigateToFolder,
  aiSidebarOpen = false,
  onToggleAISidebar,
  onOpenDownloadHistory,
  canManageFolder = false,
  onFolderDownload,
  onFolderRename,
  onFolderDelete,
  onFolderMove,
  onPhotoActionsSlotRef
}) => {
  // folderType 由调用方综合后变成 canCreateSubFolder 传过来；本组件不再判
  void folderType

  const hasRule = !!(rule && rule.exist)
  const ruleNode = hasRule ? renderRuleText(rule) : null
  const rulePlainText = hasRule ? buildRuleText(rule) : ''

  /**
   * 当前文件夹的操作菜单 · 由面包屑当前节点 caret-down 触发
   *  · 下载始终展示（与列表页右键菜单一致）
   *  · 重命名 / 移动 / 删除 仅 canManageFolder=true 时展示
   */
  const folderActionMenu = (
    <Menu
      onClick={({ key, domEvent }) => {
        domEvent.stopPropagation()
        switch (key) {
          case 'download':
            onFolderDownload && onFolderDownload()
            break
          case 'rename':
            onFolderRename && onFolderRename()
            break
          case 'move':
            onFolderMove && onFolderMove()
            break
          case 'delete':
            onFolderDelete && onFolderDelete()
            break
          default:
            break
        }
      }}
    >
      {onFolderDownload && (
        <Menu.Item key="download">
          <DownloadOutlined /> 下载
        </Menu.Item>
      )}
      {canManageFolder && onFolderRename && (
        <Menu.Item key="rename">
          <EditOutlined /> 重命名
        </Menu.Item>
      )}
      {canManageFolder && onFolderMove && (
        <Menu.Item key="move">
          <FolderOutlined /> 移动到
        </Menu.Item>
      )}
      {canManageFolder && onFolderDelete && <Menu.Divider />}
      {canManageFolder && onFolderDelete && (
        <Menu.Item key="delete" style={{ color: '#ef4444' }}>
          <DeleteOutlined /> 删除
        </Menu.Item>
      )}
    </Menu>
  )

  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <div className={styles.titleArea}>
          {/* 面包屑：照片分类(根) / 父A / 父B / ... / 当前 ▾
              · 所有节点同字号；用颜色 / 字重区分（祖先=灰常规，当前=深色加粗）
              · 当前节点是 Dropdown trigger · 点击展菜单（下载 / 重命名 / 移动 / 删除）
              · 设计 #1: 不再有左侧返回按钮（"照片分类"链接代替返回） */}
          <div className={styles.breadcrumbRow}>
            <Breadcrumb className={styles.breadcrumb} separator="/">
              <Breadcrumb.Item>
                <a
                  onClick={onBack}
                  className={classNames(
                    styles.breadcrumbLink,
                    styles.breadcrumbLinkFirst
                  )}
                >
                  照片分类
                </a>
              </Breadcrumb.Item>
              {parentFolders.map((p) => (
                <Breadcrumb.Item key={p.folderID}>
                  <a
                    onClick={() => onNavigateToFolder(p.folderID)}
                    className={styles.breadcrumbLink}
                    title={p.folderName}
                  >
                    {p.folderName}
                  </a>
                </Breadcrumb.Item>
              ))}
              <Breadcrumb.Item>
                <Dropdown
                  popupRender={() => folderActionMenu}
                  trigger={['click']}
                  placement="bottomLeft"
                >
                  <span
                    className={styles.breadcrumbCurrent}
                    title={folderName}
                    role="button"
                    aria-label={`${folderName || ''} 操作菜单`}
                  >
                    <span className={styles.breadcrumbCurrentText}>
                      {folderName || '—'}
                    </span>
                    <CaretDownOutlined
                      className={styles.breadcrumbCurrentCaret}
                    />
                  </span>
                </Dropdown>
              </Breadcrumb.Item>
            </Breadcrumb>
            {/* 张数 · 紧跟面包屑后面，弱化的灰色文字（子文件夹个数下面 section 自带，不再展示） */}
            {photoCount > 0 && (
              <span className={styles.breadcrumbPhotoCount}>
                {photoCount.toLocaleString('en-US')} 张
              </span>
            )}
          </div>
          {/* 规则提示条 · 纯展示（Tooltip 看全文），规则编辑入口走 AI 助手会话 */}
          {hasRule && (
            <div className={styles.meta}>
              <Tooltip title={rulePlainText} placement="bottom">
                <span className={classNames(styles.metaItem, styles.ruleTag)}>
                  <span className={styles.ruleStar}>✦</span>
                  <span className={styles.ruleText}>{ruleNode}</span>
                </span>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      <div className={styles.headerRight}>
        {/* 多选模式 · 头部按钮区改成 [批量操作 portal 槽位] + [取消]
            空 span 由 onPhotoActionsSlotRef 暴露给 Detail/index.tsx 做照片多选 createPortal */}
        {photoSelectMode && <span ref={onPhotoActionsSlotRef} />}
        {/* 多选 · 仅在有照片时显示；管理态切 ghost primary（与列表页文件夹多选一致） */}
        {canSelectPhotos && (
          <Button
            type={photoSelectMode ? 'primary' : 'default'}
            ghost={photoSelectMode}
            onClick={onTogglePhotoSelect}
          >
            {photoSelectMode ? '取消' : '多选'}
          </Button>
        )}
        {/* 多选模式下隐藏其他写动作按钮，避免误触
            常规态按钮组对齐列表页（Figma 14:6054）：
            添加照片 / 新建文件夹 / AI 智能分类 / ⋯ 更多 */}
        {!photoSelectMode && (
          <>
            <Button icon={<PlusOutlined />} onClick={onAddPhoto}>
              添加照片
            </Button>
            {canCreateSubFolder && (
              <Button icon={<FolderAddOutlined />} onClick={onCreateSubFolder}>
                新建文件夹
              </Button>
            )}
            {/* AI 智能分类 · sidebar 已开时隐藏避免冗余；样式与列表页一致（实底主按钮） */}
            {!aiSidebarOpen && onToggleAISidebar && (
              <AITriggerButton onClick={onToggleAISidebar} />
            )}
            {/* ⋯ 更多 · 与列表页同款菜单（下载记录走详情页埋点；详情页无回收站入口） */}
            <MoreActionsMenu onOpenDownloadHistory={onOpenDownloadHistory} />
          </>
        )}
      </div>
    </div>
  )
}

export default DetailHeader
